import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Star, ArrowRight } from "lucide-react";
import reviewsData from "@/data/reviews.json";

// Reusable testimonial quote-card that sits next to a ContactForm on product,
// industry, and partner pages. Picks the highest-rated review that matches the
// page's product/industry tag; falls back to the longest 5★ review globally.
//
// Designed to feel like a trust anchor beside the form — small, confident,
// no "Read more" button (the full /reviews page is one click away via the
// CTA link at the bottom).

function Stars({ rating = 5, size = 14 }) {
  const n = Math.max(0, Math.min(5, Math.round(Number(rating) || 5)));
  return (
    <div className="flex items-center gap-0.5" aria-label={`${n} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          size={size}
          className={s <= n ? "fill-amber-400 text-amber-400" : "text-brand-light-grey"}
        />
      ))}
    </div>
  );
}

function Avatar({ name }) {
  const initial = (name || "G").trim().charAt(0).toUpperCase();
  return (
    <div
      className="w-10 h-10 rounded-full bg-brand-aqua-light flex items-center justify-center text-brand-aqua-dark font-heading font-bold flex-shrink-0"
      aria-hidden="true"
    >
      {initial}
    </div>
  );
}

function pickBest({ industry, product, reviews }) {
  const tagMatch = (r) =>
    (industry && Array.isArray(r.industries) && r.industries.includes(industry)) ||
    (product && Array.isArray(r.products) && r.products.includes(product));

  const candidates = reviews
    .filter((r) => tagMatch(r) && r.rating >= 5 && (r.text || "").length >= 60);
  if (candidates.length > 0) {
    // Prefer the most substantive review (longer = more genuine-looking).
    return [...candidates].sort((a, b) => (b.text?.length || 0) - (a.text?.length || 0))[0];
  }
  // Global fallback — any 5★ review with decent substance.
  const fallback = reviews
    .filter((r) => r.rating >= 5 && (r.text || "").length >= 80)
    .sort((a, b) => (b.text?.length || 0) - (a.text?.length || 0));
  return fallback[0] || reviews[0] || null;
}

/**
 * @param {string}  [industry]  Industry tag for matching.
 * @param {string}  [product]   Product tag for matching.
 * @param {string}  [label]     Optional eyebrow above the tile, e.g. "What customers say".
 * @param {string}  [testId]    data-testid prefix.
 */
export default function FormReviewCompanion({
  industry,
  product,
  label = "What our customers say",
  testId = "form-review-companion",
}) {
  const review = useMemo(
    () => pickBest({ industry, product, reviews: reviewsData.reviews }),
    [industry, product]
  );

  // First-paragraph preview kept punchy — form companions shouldn't hog
  // attention away from the form itself. Must run unconditionally to keep
  // hook order stable; handles null review by returning an empty string.
  const preview = useMemo(() => {
    if (!review) return "";
    const firstPara = (review.text || "").split(/\n{2,}/)[0].trim();
    if (firstPara.length <= 220) return firstPara;
    return firstPara.slice(0, 217).trimEnd() + "…";
  }, [review]);

  if (!review) return null;

  return (
    <aside
      className="bg-brand-aqua-light/40 border border-brand-aqua/20 rounded-2xl p-5 sm:p-6"
      data-testid={testId}
      aria-label="Customer testimonial"
    >
      <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-brand-aqua-dark mb-3">
        {label}
      </p>

      <Stars rating={review.rating} size={14} />

      <blockquote className="mt-3 text-[15px] text-brand-black/90 leading-relaxed">
        {`“${preview}”`}
      </blockquote>

      <div className="mt-4 pt-4 border-t border-brand-aqua/20 flex items-center gap-3">
        <Avatar name={review.author} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-brand-black truncate">{review.author}</p>
          <p className="text-[11px] text-brand-grey">
            {`${review.dateLabel || "Verified review"} · ${review.source === "trustpilot" ? "Trustpilot" : "Google"}`}
          </p>
        </div>
      </div>

      <Link
        to="/reviews"
        className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-brand-aqua-dark hover:text-brand-black transition-colors group"
        data-testid={`${testId}-see-all`}
      >
        {`See all ${reviewsData.totals.total} verified reviews`}
        <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5" />
      </Link>
    </aside>
  );
}
